import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Plus, Shield, Truck, Star, Heart } from "lucide-react";

/**
 * Homepage hero — replaces the old gradient banner with real toy photography,
 * dual CTAs ("Browse Toys" / "List a Toy"), and a trust-badge row.
 *
 * Design notes:
 * - The headline can be A/B-tested via the experiment payload passed in.
 * - The hero image is a public Unsplash photo licensed for commercial reuse,
 *   loaded at low priority via fetchPriority="low" so it never blocks LCP.
 * - The image column collapses below the copy on mobile (<lg).
 */
const HERO_IMAGE_DESKTOP =
  "https://images.unsplash.com/photo-1545558014-8692077e9b5c?auto=format&fit=crop&w=1200&q=70";
const HERO_IMAGE_MOBILE =
  "https://images.unsplash.com/photo-1545558014-8692077e9b5c?auto=format&fit=crop&w=640&q=60";

const TRUST_BADGES = [
  { icon: <Shield  className="w-4 h-4" />, label: "Buyer protection",   testid: "trust-badge-protection" },
  { icon: <Truck   className="w-4 h-4" />, label: "Royal Mail tracked", testid: "trust-badge-shipping"  },
  { icon: <Star    className="w-4 h-4" />, label: "Verified sellers",   testid: "trust-badge-verified"  },
  { icon: <Heart   className="w-4 h-4" />, label: "UK family-owned",    testid: "trust-badge-uk"        },
];

export const HeroSection = ({
  headline = "Give every toy a second chance.",
  subheadline = "Buy, sell, swap, or donate pre-loved toys with families across the UK — safely, sustainably, and for a fraction of retail.",
  isLoggedIn = false,
  experimentVariant,
}) => (
  <section
    className="relative bg-[#FDFBF7] border-2 border-gray-900 rounded-3xl overflow-hidden mb-8 shadow-[6px_6px_0_0_#111827]"
    data-testid={experimentVariant ? `hero-${experimentVariant}` : "hero-section"}
  >
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
      {/* Copy column */}
      <div className="px-6 sm:px-10 pt-10 lg:pt-14 pb-8 lg:pb-14 order-2 lg:order-1">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD93D] border-2 border-gray-900 text-xs font-black uppercase tracking-wide mb-5"
          data-testid="hero-eyebrow"
        >
          <Heart className="w-3 h-3" /> 5,000+ toys saved from landfill
        </div>

        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight mb-5"
          style={{ fontFamily: "Outfit" }}
          data-testid="hero-headline"
        >
          {headline}
        </h1>

        <p
          className="text-base sm:text-lg text-gray-700 leading-relaxed max-w-xl mb-7"
          data-testid="hero-subheadline"
        >
          {subheadline}
        </p>

        <div className="flex flex-wrap gap-3 mb-8">
          <Link
            to="/browse"
            data-testid="hero-cta-browse"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#FF6B6B] text-white font-black uppercase tracking-wide border-2 border-gray-900 shadow-[4px_4px_0_0_#111827] hover:translate-y-0.5 hover:shadow-[2px_2px_0_0_#111827] transition"
          >
            <ShoppingCart className="w-4 h-4" />
            Browse Toys
          </Link>
          <Link
            to={isLoggedIn ? "/create-listing" : "/auth?intent=sell"}
            data-testid="hero-cta-list"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-gray-900 font-black uppercase tracking-wide border-2 border-gray-900 shadow-[4px_4px_0_0_#111827] hover:translate-y-0.5 hover:shadow-[2px_2px_0_0_#111827] transition"
          >
            <Plus className="w-4 h-4" />
            List a Toy
          </Link>
        </div>

        {/* Trust badges */}
        <ul className="flex flex-wrap gap-x-5 gap-y-2" data-testid="hero-trust-badges">
          {TRUST_BADGES.map((b) => (
            <li
              key={b.testid}
              data-testid={b.testid}
              className="inline-flex items-center gap-2 text-sm font-bold text-gray-700"
            >
              <span className="w-7 h-7 rounded-full bg-white border-2 border-gray-900 flex items-center justify-center text-gray-900">
                {b.icon}
              </span>
              {b.label}
            </li>
          ))}
        </ul>
      </div>

      {/* Image column */}
      <div className="relative order-1 lg:order-2 h-56 sm:h-72 lg:h-full min-h-[260px]">
        <picture>
          <source media="(min-width: 640px)" srcSet={HERO_IMAGE_DESKTOP} />
          <img
            src={HERO_IMAGE_MOBILE}
            alt="A joyful collection of pre-loved children's toys ready for a second life."
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-image"
          />
        </picture>
        {/* Soft fade on the inner edge for desktop so copy & image visually connect */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#FDFBF7] to-transparent"
        />
      </div>
    </div>
  </section>
);
